
import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import {
  SidebarHeader,
  SidebarNavigation,
  SidebarFooter
} from './sidebar';
import navItems from './sidebar/navItems';

type SidebarProps = {
  className?: string;
  collapsed?: boolean;
  isMobile?: boolean;
  onNavigation?: () => void;
  onToggleCollapse?: () => void;
};

const Sidebar = ({ 
  className, 
  collapsed = false, 
  isMobile = false, 
  onNavigation,
  onToggleCollapse 
}: SidebarProps) => {
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>({});
  const location = useLocation();
  
  // Auto-expand parent items when child routes are active
  useEffect(() => {
    navItems.forEach(item => {
      if (item.children) {
        const isChildActive = item.children.some(child => 
          location.pathname === child.path || 
          (child.path !== '/dashboard' && location.pathname.startsWith(child.path))
        );
        
        if (isChildActive) {
          setExpandedItems(prev => ({
            ...prev,
            [item.title]: true
          }));
        }
      }
    });
  }, [location.pathname]);
  
  const handleNavigation = () => {
    if (isMobile && onNavigation) {
      onNavigation();
    }
  };

  const toggleExpandItem = (title: string, event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    
    setExpandedItems(prev => ({
      ...prev,
      [title]: !prev[title]
    }));
  };

  const handleToggleExpand = () => {
    if (onToggleCollapse) {
      onToggleCollapse();
    }
  };
  
  return (
    <aside 
      className={cn(
        "fixed left-0 top-0 h-screen bg-white dark:bg-background border-r border-border transition-all duration-300 ease-in-out z-30 flex flex-col",
        collapsed ? "w-20" : "w-64",
        isMobile && "w-full h-full",
        className
      )}
    >
      <SidebarHeader 
        collapsed={collapsed} 
        isMobile={isMobile} 
        onToggleExpand={handleToggleExpand} 
      />
      
      <div className="flex-1 flex flex-col h-[calc(100vh-8rem)] overflow-hidden">
        <SidebarNavigation 
          items={navItems}
          expanded={!collapsed}
          isMobile={isMobile}
          expandedItems={expandedItems}
          toggleExpandItem={toggleExpandItem}
          handleNavigation={handleNavigation}
        />
      </div>
      
      <SidebarFooter 
        expanded={!collapsed} 
        isMobile={isMobile} 
        onNavigation={handleNavigation} 
      />
    </aside>
  );
};

export default Sidebar;
